import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogActions,
    IconButton,
    makeStyles,
    Tab,
    Tabs,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import { FormikProvider, useFormik } from 'formik';
import i18n from 'i18n-js';

import { Reservations, ReservationStatus, SeatedEditConflict } from 'api/controllers/reservations';
import { PopupConfirmation } from 'components/shared';
import { SignalREvent, signalRService } from 'services/signalR';
import { theme } from 'config/theme';
import {
    canConfirmReservation,
    findDuplicateReservation,
    formatFormGuestsCount,
    formatGuestsLabel,
    formatReservationDate,
    formatSeats,
    getDefaultDurationMinutes,
    getRestaurantWallClockString,
    getMaxGuestsOnline,
    getSelectedSeats,
    getUnavailableTables,
    isOutsideOpeningHours,
    keepFreeTables,
    minutesToTime,
    ReservationFormErrors,
    ReservationFormValues,
    timeToMinutes,
    toWireDateTime,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useReservationMaps } from '../hooks';
import ReservationFormFields from './ReservationFormFields';
import ReservationTableMap, { TABLE_STATUS_COLORS } from './ReservationTableMap';
import { ReservationStatusChip } from './ReservationDetailsPanel';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
        borderRadius: 4,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        height: 49,
        padding: '0 20px',
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
    },
    tab: {
        letterSpacing: 1.25,
        maxWidth: 'none',
    },
    cancel: {
        marginLeft: 'auto',
    },
    body: {
        display: 'flex',
        flex: 1,
        minHeight: 0,
    },
    mapColumn: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
        padding: '8px 20px 16px',
    },
    mapToolbar: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        marginBottom: 20,
    },
    legend: {
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        marginLeft: 'auto',
        fontSize: 14,
        whiteSpace: 'nowrap',
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    legendDot: {
        width: 12,
        height: 12,
        borderRadius: '50%',
    },
    mapContainer: {
        flex: 1,
        minHeight: 0,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 4,
        overflow: 'hidden',
    },
    summary: {
        fontSize: 14,
        fontWeight: 500,
        marginTop: 16,
    },
    hint: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: 4,
    },
    panel: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        width: 460,
        borderLeft: `1px solid ${theme.palette.secondary.light}`,
        backgroundColor: 'rgba(231, 231, 231, 0.3)',
    },
    panelContent: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: 20,
    },
    actionBar: {
        flexShrink: 0,
        minHeight: 108,
        padding: '12px 20px 20px',
        backgroundColor: '#f8f8f8',
        borderTop: `1px solid ${theme.palette.grey[300]}`,
    },
    tableRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        minHeight: 32,
        marginBottom: 12,
    },
    tableCaption: {
        fontSize: 10,
        letterSpacing: 1.25,
        textTransform: 'uppercase',
        color: theme.palette.common.black,
        marginRight: 8,
    },
    tableNote: {
        fontSize: 14,
        color: theme.palette.text.secondary,
    },
    seatsCounter: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    actions: {
        display: 'flex',
        gap: 16,
        '& > *': {
            flex: 1,
        },
    },
});

interface ReservationEditorProps {
    status?: ReservationStatus;
    isEditing?: boolean;
    title: string;
    channelLine: string | null;
    reservationId: string | null;
    initialValues: ReservationFormValues;
    validate: (values: ReservationFormValues, now: string) => ReservationFormErrors;
    confirmLabel: string;
    onCancel: () => void;
    onSave?: (values: ReservationFormValues) => Promise<void>;
    onConfirm: (values: ReservationFormValues) => Promise<string | SeatedEditConflict | null>;
}

export default function ReservationEditor({
    title,
    channelLine,
    reservationId,
    initialValues,
    validate,
    confirmLabel,
    onCancel,
    onSave,
    onConfirm,
    isEditing = false,
    status,
}: ReservationEditorProps) {
    const classes = useStyles();
    const dispatch = useThunkDispatch();
    const settings = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.reservationModule
    );
    const requests = useAppSelector((state) => state.reservations.requests);
    const active = useAppSelector((state) => state.reservations.active);
    const availability = useAppSelector((state) => state.reservations.availability);
    const timeZone = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.timeZone
    );
    const dineInHours = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.dineIn?.workingHours
    );
    const { maps, tables } = useReservationMaps();
    const [activeMapId, setActiveMapId] = useState<string | null>(null);
    const [discardOpen, setDiscardOpen] = useState(false);
    const [blockedTable, setBlockedTable] = useState<string | null>(null);
    const [isCheckingOrder, setCheckingOrder] = useState(false);
    const checkingOrder = useRef(false);
    const isSeated = status === 'Seated';
    const submitAction = useRef<'save' | 'confirm'>('save');

    const formik = useFormik<ReservationFormValues>({
        initialValues,
        validate: (formValues) => validate(formValues, getRestaurantWallClockString(timeZone)),
        onSubmit: async (formValues) => {
            if (checkingOrder.current) return;
            if (submitAction.current === 'save' && onSave) {
                await onSave(formValues);
                return;
            }
            const takenTableId = await onConfirm(formValues);
            if (!takenTableId) {
                return;
            }
            if (typeof takenTableId !== 'string') {
                if (takenTableId.status === 'removedTableOrderOpen') {
                    formik.setFieldValue(
                        'tableIds',
                        formValues.tableIds.includes(takenTableId.tableId)
                            ? formValues.tableIds
                            : formValues.tableIds.concat(takenTableId.tableId)
                    );
                    setBlockedTable(takenTableId.tableId);
                } else {
                    dispatch(globalStore.actionCreators.showToaster('error', takenTableId.message));
                }
                loadAvailability(formValues);
                return;
            }
            const label = tables.find((table) => table.id === takenTableId)?.label ?? '';
            if (!isSeated || !initialValues.tableIds.includes(takenTableId)) {
                formik.setFieldValue(
                    'tableIds',
                    formValues.tableIds.filter((id) => id !== takenTableId)
                );
            }
            dispatch(
                globalStore.actionCreators.showToaster(
                    'error',
                    i18n.t('reservations.tableNoLongerAvailable', {
                        table: label,
                        time: formValues.time,
                    })
                )
            );
            loadAvailability(formValues);
        },
    });
    const { values, setFieldValue, isSubmitting } = formik;
    const previousGuestsCount = useRef(values.guestsCount);
    const cancel = () => {
        if (isSubmitting || checkingOrder.current) return;
        if (isEditing && formik.dirty) setDiscardOpen(true);
        else onCancel();
    };

    function loadAvailability({ date, time, durationMinutes }: ReservationFormValues) {
        if (!date || !time || !durationMinutes) {
            dispatch(reservationsStore.actionCreators.clearTablesAvailability());
            return;
        }
        dispatch(
            reservationsStore.actionCreators.getTablesAvailability(
                toWireDateTime(date, time),
                durationMinutes,
                reservationId
            )
        );
    }

    useEffect(() => {
        if (values.date) {
            dispatch(reservationsStore.actionCreators.getActiveReservations(values.date));
        }
    }, [values.date]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadAvailability(values);
    }, [values.date, values.time, values.durationMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isSeated) return;
        const refresh = () => loadAvailability(values);
        const added = signalRService.subscribe(SignalREvent.OrderAdded, refresh);
        const changed = signalRService.subscribe(SignalREvent.OrderChanged, refresh);
        const reconnected = signalRService.subscribeReconnected(refresh);
        window.addEventListener('focus', refresh);
        return () => {
            added();
            changed();
            reconnected();
            window.removeEventListener('focus', refresh);
        };
    }, [isSeated, values.date, values.time, values.durationMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

    const unavailableTables = useMemo(() => {
        const isCurrent =
            availability.key ===
            [toWireDateTime(values.date, values.time), values.durationMinutes, reservationId].join(
                '|'
            );
        const unavailable = isCurrent ? getUnavailableTables(availability.tables) : {};
        if (isSeated) {
            tables.forEach((table) => {
                if (!isCurrent || !availability.tables.some((item) => item.tableId === table.id)) {
                    unavailable[table.id] = null;
                }
            });
        }
        return unavailable;
    }, [
        availability,
        values.date,
        values.time,
        values.durationMinutes,
        reservationId,
        isSeated,
        tables,
    ]);

    useEffect(() => {
        if (isSeated) return;
        const freeTableIds = keepFreeTables(values.tableIds, unavailableTables);
        if (freeTableIds.length !== values.tableIds.length) {
            setFieldValue('tableIds', freeTableIds);
        }
    }, [unavailableTables]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (previousGuestsCount.current === values.guestsCount) return;
        previousGuestsCount.current = values.guestsCount;
        if (
            !isSeated &&
            values.guestsCount !== null &&
            getSelectedSeats(values.tableIds, tables) < values.guestsCount
        ) {
            setFieldValue('tableIds', []);
        }
    }, [values.guestsCount]); // eslint-disable-line react-hooks/exhaustive-deps

    const outsideOpeningHours = useMemo(
        () =>
            isOutsideOpeningHours(
                values.date,
                values.time,
                dineInHours,
                values.durationMinutes ?? 0
            ),
        [values.date, values.time, values.durationMinutes, dineInHours]
    );

    if (!settings) {
        return null;
    }

    const activeMap = maps.find((map) => map.id === activeMapId) ?? maps[0];
    const selectedTables = tables.filter((table) => values.tableIds.includes(table.id));
    const selectedSeats = getSelectedSeats(values.tableIds, tables);
    const duplicate = findDuplicateReservation(
        values,
        values.date,
        requests.concat(active.reservations),
        reservationId
    );
    const isInitiallyMoreThanMaxGuests =
        initialValues.isMoreThanMaxGuests && initialValues.guestsCount === null;
    const maxGuestsOnline = getMaxGuestsOnline(settings);

    const toggleTable = async (tableId: string) => {
        if (isSubmitting || checkingOrder.current) return;
        if (values.tableIds.includes(tableId)) {
            if (isSeated && reservationId) {
                checkingOrder.current = true;
                setCheckingOrder(true);
                try {
                    let hasOrder: boolean;
                    if (initialValues.tableIds.includes(tableId)) {
                        const orderStates = await Reservations.getAssignedTablesOrderState(
                            reservationId
                        );
                        const table = orderStates.find((item) => item.tableId === tableId);
                        if (!table) throw new Error('Assigned table is missing from order check.');
                        hasOrder = table.hasActiveOrder;
                    } else {
                        const availability = await Reservations.getTablesAvailability(
                            toWireDateTime(values.date, values.time),
                            values.durationMinutes ?? 0,
                            reservationId
                        );
                        const table = availability.find((item) => item.tableId === tableId);
                        if (!table) throw new Error('Selected table is missing from order check.');
                        hasOrder = table.hasActiveOrder;
                    }
                    if (hasOrder) {
                        setBlockedTable(tableId);
                        return;
                    }
                } catch (error) {
                    dispatch(globalStore.actionCreators.showToaster('error', error));
                    return;
                } finally {
                    checkingOrder.current = false;
                    setCheckingOrder(false);
                }
            }
            setFieldValue(
                'tableIds',
                values.tableIds.filter((id) => id !== tableId)
            );
            return;
        }
        if (tableId in unavailableTables) return;
        setFieldValue('tableIds', values.tableIds.concat(tableId));
    };

    const submit = (action: 'save' | 'confirm') => {
        submitAction.current = action;
        formik.submitForm();
    };

    const summary = [
        values.date ? formatReservationDate(values.date, 'EEE d MMM') : '',
        values.time
            ? `${values.time} – ${minutesToTime(
                  timeToMinutes(values.time) + (values.durationMinutes ?? 0)
              )}`
            : '',
        formatGuestsLabel(formatFormGuestsCount(values, maxGuestsOnline)),
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <IconButton
                    size="small"
                    color="primary"
                    disabled={isSubmitting || isCheckingOrder}
                    onClick={cancel}
                    aria-label={i18n.t('button.cancel')}
                >
                    {isEditing ? <CloseIcon /> : <ArrowBackIcon />}
                </IconButton>
                <Tabs value={0} indicatorColor="primary" textColor="primary">
                    <Tab className={classes.tab} label={title} />
                </Tabs>
                {isSeated && <ReservationStatusChip status="Seated" />}
                <Button
                    className={classes.cancel}
                    variant="outlined"
                    color="primary"
                    disabled={isSubmitting || isCheckingOrder}
                    onClick={cancel}
                >
                    {i18n.t('button.cancel')}
                </Button>
            </div>
            <div className={classes.body}>
                <div className={classes.mapColumn}>
                    <div className={classes.mapToolbar}>
                        <Tabs
                            value={activeMap ? activeMap.id : false}
                            onChange={(_event, mapId: string) => setActiveMapId(mapId)}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {maps.map((map) => (
                                <Tab
                                    key={map.id}
                                    className={classes.tab}
                                    value={map.id}
                                    label={map.name}
                                />
                            ))}
                        </Tabs>
                        <div className={classes.legend}>
                            {(['free', 'booked'] as const).map((status) => (
                                <span key={status} className={classes.legendItem}>
                                    <span
                                        className={classes.legendDot}
                                        style={{ backgroundColor: TABLE_STATUS_COLORS[status] }}
                                    />
                                    {i18n.t(`reservations.${status}`)}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className={classes.mapContainer}>
                        {activeMap && (
                            <ReservationTableMap
                                map={activeMap}
                                unavailableTables={unavailableTables}
                                selectedTableIds={values.tableIds}
                                onToggleTable={toggleTable}
                            />
                        )}
                    </div>
                    <div className={classes.summary}>{summary}</div>
                    <div className={classes.hint}>{i18n.t('reservations.mapHint')}</div>
                </div>
                <div className={classes.panel}>
                    <div className={classes.panelContent}>
                        <FormikProvider value={formik}>
                            <ReservationFormFields
                                seatedStart={
                                    isSeated
                                        ? `${initialValues.date}T${initialValues.time}`
                                        : undefined
                                }
                                channelLine={channelLine}
                                isEditing={isEditing}
                                defaultDurationMinutes={getDefaultDurationMinutes(settings)}
                                notice={
                                    isInitiallyMoreThanMaxGuests
                                        ? i18n.t('reservations.moreThanMaxGuestsNotice', {
                                              max: maxGuestsOnline,
                                          })
                                        : null
                                }
                                duplicateWarning={
                                    duplicate
                                        ? i18n.t('reservations.duplicate', {
                                              name: [duplicate.firstName, duplicate.lastName]
                                                  .filter(Boolean)
                                                  .join(' '),
                                          })
                                        : null
                                }
                                isOutsideOpeningHours={outsideOpeningHours}
                            />
                        </FormikProvider>
                    </div>
                    <div className={classes.actionBar}>
                        <div className={classes.tableRow}>
                            <span className={classes.tableCaption}>
                                {i18n.t('reservations.sections.table')}
                            </span>
                            {selectedTables.length === 0 && (
                                <span className={classes.tableNote}>
                                    {i18n.t('reservations.noTableChosen')}
                                </span>
                            )}
                            {selectedTables.map((table) => (
                                <Chip
                                    key={table.id}
                                    label={i18n.t('reservations.tableChip', {
                                        area: table.areaName,
                                        table: table.label,
                                        seats: formatSeats(table.seats),
                                    })}
                                    onDelete={() => toggleTable(table.id)}
                                />
                            ))}
                            {selectedTables.length > 0 && values.guestsCount !== null && (
                                <span className={classes.seatsCounter}>
                                    {i18n.t('reservations.selectedSeats', {
                                        selected: selectedSeats,
                                        total: values.guestsCount,
                                    })}
                                </span>
                            )}
                        </div>
                        <div className={classes.actions}>
                            {onSave && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    disabled={isSubmitting}
                                    onClick={() => submit('save')}
                                >
                                    {i18n.t('button.save')}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={
                                    isSubmitting ||
                                    isCheckingOrder ||
                                    (isEditing && !formik.dirty) ||
                                    !canConfirmReservation(values, selectedSeats)
                                }
                                onClick={() => submit('confirm')}
                            >
                                {confirmLabel}
                            </Button>
                        </div>
                        <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)}>
                            <DialogTitle>{i18n.t('reservations.discardChanges')}</DialogTitle>
                            <DialogActions>
                                <Button color="primary" onClick={() => setDiscardOpen(false)}>
                                    {i18n.t('reservations.keepEditing')}
                                </Button>
                                <Button color="primary" onClick={onCancel}>
                                    {i18n.t('reservations.discard')}
                                </Button>
                            </DialogActions>
                        </Dialog>
                        {isSeated && (
                            <PopupConfirmation
                                open={blockedTable !== null}
                                close={() => setBlockedTable(null)}
                                title={i18n.t('reservations.removeTableOrderTitle', {
                                    table:
                                        tables.find((table) => table.id === blockedTable)?.label ??
                                        '',
                                })}
                                description={i18n.t(
                                    values.tableIds.some((id) => id !== blockedTable)
                                        ? 'reservations.removeTableOrderDescription'
                                        : 'reservations.removeLastTableOrderDescription',
                                    {
                                        table:
                                            tables.find((table) => table.id === blockedTable)
                                                ?.label ?? '',
                                        otherTable:
                                            tables.find(
                                                (table) =>
                                                    table.id !== blockedTable &&
                                                    values.tableIds.includes(table.id)
                                            )?.label ?? '',
                                    }
                                )}
                                activeBtn="OK"
                                action={() => setBlockedTable(null)}
                                hideCancel
                                basicButton
                                isDismissible
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
